import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PaginationBase } from '../../../core/class/pagination-base';
import { TaxAccountingRecords } from '../tax-accounting-records.component/tax-accounting-records';
import { TaxAccountingHeader } from '../tax-accounting-header/tax-accounting-header';
import { AuditTrailTableDTO } from '../../../core/interface/audit-trail.interface';
import { AppModalService } from '../../../core/services/app-modal.service';
import {
  IssueEditedValueDTO,
  IssueEditFieldsDTO,
  TaxAccountingAuditTrail,
  TaxAccountingSaveRequest,
} from '../../../core/interface/taxAccounting.interface';
import { UserDetails } from '../../../core/interface/app-user.interface';
import { TaxAccountingService } from '../../../core/services/tax-accounting.service';
import { ApiService } from '../../../core/services/app-api.service';

@Component({
  selector: 'app-tax-accounting-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, TaxAccountingRecords, TaxAccountingHeader],
  templateUrl: './tax-accounting-edit.html',
  styleUrl: './tax-accounting-edit.scss',
})
export class TaxAccountingEdit
  extends PaginationBase<AuditTrailTableDTO>
  implements OnInit, OnDestroy
{
  @ViewChild('taxAccEDIT')
  taxAccEDIT!: ElementRef<HTMLElement>;
  @ViewChild('recordsContainer') taxAccountingRecords!: TaxAccountingRecords;
  isAccountingEdit: boolean = false;
  issue: any = null;
  dragging = false;
  offsetX = 0;
  offsetY = 0;
  positionX = 0;
  positionY = 0;
  override data: AuditTrailTableDTO[] = [];
  private subscription?: Subscription;
  private readonly taxAccountingService = inject(TaxAccountingService);
  private readonly appService = inject(ApiService);
  tbbsStatus: string[] = [];

  constructor(private readonly appModalService: AppModalService) {
    super();
  }

  ngOnInit(): void {
    this.subscription = this.appModalService.taxAccountingEditSave$.subscribe((issue) => {
      this.issue = structuredClone(issue.issue);
      this.isAccountingEdit = true;
      setTimeout(() => {
        this.centerModal();
      });
    });

    this.subscription = this.taxAccountingService.tbbsStatus.subscribe((status) => {
      this.tbbsStatus = status;
    });
  }

  get issueRow(): any {
    return this.issue?.details?.find((x: any) => x.label === 'Issue');
  }

  saveRecord(): void {
    const issueEditData = this.taxAccountingRecords.issueEditData;
    let auditTrail: any[] = [];
    if (this.taxAccountingRecords?.hasChanges()) {
      auditTrail = this.createAuditTrail(this.taxAccountingRecords.getChangedFields());
    }
    const payload: TaxAccountingSaveRequest = {
      IssueEffect: {
        ID: this.issue?.id,
        ASC_740_IssueID: issueEditData.ascIssueId,
        ASC_740Flag: issueEditData.ascFlag,
        ASC_740_comment: issueEditData.ascComment,
        TBBS: issueEditData.tbbsDescription,
        TBBSFlag: issueEditData.tbbsFlag,
        TBBSStatus: issueEditData.tbbsStatus,
        SP_DocID: issueEditData.spLink,
        DateModification: new Date().toISOString(),
      },
      AuditTrail: auditTrail,
    };
    this.appService.saveTaxAccountingRecord(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.taxAccountingService.triggerFetchTaxAccountingList(true);
        }
        this.closeForm();
      },
      error: (error) => {
        console.error('Error saving tax accounting record:', error);
      },
    });
  }

  private createAuditTrail(changes: IssueEditedValueDTO[]): TaxAccountingAuditTrail[] {
    const fieldNames: Record<keyof IssueEditFieldsDTO, string> = {
      ascFlag: 'ASC_740Flag',
      ascIssueId: 'ASC_740_IssueID',
      ascComment: 'ASC_740_comment',
      tbbsFlag: 'TBBSFlag',
      tbbsStatus: 'TBBSStatus',
      tbbsDescription: 'TBBS',
      spLink: 'SP_DocID',
    };
    const auditDateTime = new Date().toISOString();
    const userName = this.getCurrentUserEmail();

    return changes.map((change) => ({
      AuditDateTime: auditDateTime,
      UserName: userName,
      FormName: 'update_ASC',
      RecordID: String(this.issue.id),
      FieldName: fieldNames[change.field],
      OldValue: this.formatAuditValue(change.oldValue),
      NewValue: this.formatAuditValue(change.newValue),
    }));
  }

  private formatAuditValue(value: string | boolean): string | null {
    return value === '' ? null : String(value);
  }

  private getCurrentUserEmail(): string {
    const userJson = sessionStorage.getItem('currentUser');
    if (!userJson) {
      return '';
    }
    return (JSON.parse(userJson) as UserDetails).emailId ?? '';
  }

  closeForm(): void {
    this.isAccountingEdit = false;
    this.issue = null;
  }

  private centerModal(): void {
    if (!this.taxAccEDIT) {
      return;
    }
    const pos = this.appModalService.centerModal(this.taxAccEDIT.nativeElement);
    this.positionX = pos.positionX;
    this.positionY = pos.positionY;
  }

  startDrag(event: MouseEvent): void {
    this.appModalService.startDrag(event, this.positionX, this.positionY);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const pos = this.appModalService.drag(event);
    if (!pos) {
      return;
    }
    this.positionX = pos.positionX;
    this.positionY = pos.positionY;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.appModalService.stopDrag();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
